import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Media from "./Media";
import useScrollAccordion from "../hooks/useScrollAccordion";

/**
 * THE INDEX TABLE — used on every screen size
 * ---------------------------------------------------------------------------
 *   ─────┬─────────────────────────────────────────────
 *   #001 │ Revolution 2019
 *   ─────┼─────────────────────────────────────────────
 *   #002 │ Cadavre Exquis                            →   ← open row
 *        │ summary text…            ┌─────────────┐
 *        │                          │    cover    │
 *        │                          └─────────────┘
 *   ─────┼─────────────────────────────────────────────
 *   #003 │ …
 *
 * One row at a time is "open": it extends to show the project's summary and
 * cover image. What opens a row depends on `mode`:
 *
 *   "hover"  (mouse)  the row under the pointer / keyboard focus.
 *                     Opening & closing is animated in CSS.
 *   "scroll" (touch)  the table pins to the screen and scrolling opens the
 *                     rows one after another — see hooks/useScrollAccordion.js
 *
 * Clicking a row opens the project page. In scroll mode, tapping a row
 * that isn't open first opens it; tap again to visit.
 *
 * Anything passed as `children` (the bio) is shown above the rows. In
 * scroll mode it's pinned together with the table.
 * The look (borders, sizes, animation) is in `styles/project-table.css`.
 *
 * @param {object}            props
 * @param {Array}             props.projects  From `src/data/projects.js`.
 * @param {"hover"|"scroll"}  props.mode
 * @param {React.ReactNode}   [props.children]  Shown above the rows.
 */
export default function ProjectTable({ projects, mode, children }) {
  const isHover = mode === "hover";

  // Hover mode: the row under the mouse (null = none)
  const [hovered, setHovered] = useState(null);

  // Scroll mode: the row picked by scroll position
  const runwayRef = useRef(null);
  const pinRef = useRef(null);
  const { open: scrolled, jumpToRow } = useScrollAccordion({
    enabled: !isHover,
    runwayRef,
    pinRef,
    count: projects.length,
  });

  const open = isHover ? hovered : scrolled;

  return (
    <section className={`ptable ptable--${mode}`}>
      {/* In scroll mode these two wrappers pin the table while you scroll;
          in hover mode they're plain boxes with no styling. */}
      <div className="ptable__runway" ref={runwayRef}>
        <div className="ptable__pin" ref={pinRef}>
          {children}
          <ol
            className="ptable__rows"
            // Leaving the rows (in any direction, e.g. up onto the bio)
            // closes the open row
            onMouseLeave={() => isHover && setHovered(null)}
          >
            {projects.map((project, i) => {
              const isOpen = open === i;
              return (
                <li
                  key={project.slug}
                  className={`ptable__row ${isOpen ? "is-open" : ""}`}
                >
                  <Link
                    to={`/${project.slug}`}
                    className="ptable__link"
                    onMouseEnter={() => isHover && setHovered(i)}
                    onFocus={() => isHover && setHovered(i)}
                    onClick={(e) => {
                      // Scroll mode: the first tap on a closed row opens it
                      // (closing the others); tapping the open row follows
                      // the link to the project page.
                      if (!isHover && open !== i) {
                        e.preventDefault();
                        jumpToRow(i);
                      }
                    }}
                  >
                    <span className="ptable__index mono">{project.index}</span>

                    <span className="ptable__title">{project.title}</span>

                    <span className="ptable__arrow" aria-hidden="true">
                      →
                    </span>

                    {/* The part that extends. Collapsed when closed. */}
                    <span className="ptable__detail">
                      <span className="ptable__detail-inner">
                        <span className="ptable__summary">{project.summary}</span>
                        <span className="ptable__cover">
                          <Media item={project.cover} playing={isOpen} />
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
